// @flow
import './DataTimelineDialog.css';

import * as React from 'react';
import _ from 'lodash';
import Modal from '../modals/Modal2';
import Button from '../common/Button';
import DataTimeline from '../protocol/DataTimeline';
import TimelineDatapointForm from './TimelineDatapointForm';
import StoreListener from '../store/StoreListener';
import { PROP_NAME_TO_PROP_INFO, GROUP_CODE_TO_GROUP_INFO } from '../store/Constants';
import HashMap from '../protocol/HashMap';
import Dropdown from '../common/Dropdown';
import { KeyFrameMetadataEntry, GroupInfo } from '../autogenerated/ZoomBridge';
import VideoController from './VideoController';
import t from '../i18n/I18n';
import KeyboardShortcuts from '../keyboard-shortcuts/KeyboardShortcuts';
import KeydownConfig from '../keyboard-shortcuts/KeydownConfig';
import { KeyCode } from '../util/Constants';
import Store from '../store/Store';
import MetadataUtil from '../util/MetadataUtil';
import { ThemeSetup } from '../themes/ThemeSetup';

function transformPropNames(map: HashMap) {
  const groupCodeToGroupInfo: HashMap = Store.get(GROUP_CODE_TO_GROUP_INFO);
  let groupInfo: GroupInfo = null;
  return _.sortBy(
    map
      .toArray()
      .filter(
        (entry) =>
          (groupInfo = groupCodeToGroupInfo.get(MetadataUtil.splitGroupCodeAndPropName(entry.key).groupCode)) &&
          groupInfo &&
          groupInfo.isShowInTimeline()
      )
      .map(({ key }) => ({
        value: key,
        label: key.substring(5)
      })),
    'label'
  );
}

type Props = {
  dataTimeline: DataTimeline,
  onClose: Function,
  onSubmit: Function,
  PROP_NAME_TO_PROP_INFO: HashMap,
  videoController: VideoController,
  dataPointTarget: string
};

type State = {
  dropdownValue: string,
  options: Object[],
  addDataPointDisabled: boolean,
};

const storeItems = [PROP_NAME_TO_PROP_INFO];
const METADATA_KEY_TEMP_VALUE = '__TEMP__';

@StoreListener(storeItems)
export default class DataTimelineDialog extends React.Component<Props, State> {
  _metadataKey: string = METADATA_KEY_TEMP_VALUE;
  _validationInfos: Map<KeyFrameMetadataEntry, Object> = new Map();

  state = {
    dropdownValue: this.props.dataPointTarget || this.props.dataTimeline.name,
    options: transformPropNames(this.props.PROP_NAME_TO_PROP_INFO),
    addDataPointDisabled : false
  };

  componentDidMount() {
    this.addKeyListeners();
    if (this.props.dataPointTarget) {
      this.onNameChange(this.props.dataPointTarget);
    }
  }
  componentWillUnmount() {
    this.removeKeyListeners();
  }

  addKeyListeners = () => {
    KeyboardShortcuts.add(new KeydownConfig('DATA_TIMELINE_DIALOG', this.onSubmit, [KeyCode.ENTER], [], false));
    KeyboardShortcuts.add(new KeydownConfig('DATA_TIMELINE_DIALOG', this.props.onClose, [KeyCode.ESCAPE], [], false));
  };
  removeKeyListeners = () => {
    KeyboardShortcuts.remove('DATA_TIMELINE_DIALOG');
  };

  getKey = () => {
    return this.state.dropdownValue || this._metadataKey;
  };

  onNameChange = (dropdownValue: string) => {
    const { dataTimeline } = this.props;
    this._metadataKey = dropdownValue;
    this.setState({ dropdownValue });
    dataTimeline.entries.map((entry) => {
      const metadataMap: HashMap = entry.getMetadataMap();
      const value: string = metadataMap.get(METADATA_KEY_TEMP_VALUE);
      if (value) {
        metadataMap.put(this._metadataKey, value);
        metadataMap.remove(METADATA_KEY_TEMP_VALUE);
      }
    });
  };

  onDatapointChange = (keyFrameMetadataEntry: KeyFrameMetadataEntry, prop: string, value: string) => {
    const validationInfo = this._validationInfos.get(keyFrameMetadataEntry) || {};
    const endTimeDuration = this.props.videoController.duration * 1000;
    switch (prop) {
      case 'label':
        const map = new HashMap();
        map.put(this.getKey(), value);
        keyFrameMetadataEntry.setMetadataMap(map);
        break;
      case 'startTimeMs':
        if (value === null) {
          validationInfo[prop] = false;
          this.setState({addDataPointDisabled :true});
        } else {
          const endTime = keyFrameMetadataEntry.getEndTime();
          if (value > endTime) {
            validationInfo[prop] = false;
            this.setState({addDataPointDisabled :true});
            keyFrameMetadataEntry.setStartTime(value);
          } else {
            validationInfo[prop] = true;
            validationInfo['endTimeMs'] = true;
            this.setState({addDataPointDisabled :false});
            keyFrameMetadataEntry.setStartTime(value);
          }
        }
        break;
      case 'stopTimeMs':
        if (value === null) {
          validationInfo[prop] = false;
          this.setState({addDataPointDisabled :true});
        } else {
          const startTime = keyFrameMetadataEntry.getStartTime();
          if (startTime > value || endTimeDuration < value) {
            validationInfo[prop] = false;
            this.setState({addDataPointDisabled :true});
            keyFrameMetadataEntry.setEndTime(value);
          } else {
            validationInfo[prop] = true;
            validationInfo['startTimeMs'] = true;
            this.setState({addDataPointDisabled :false});
            keyFrameMetadataEntry.setEndTime(value);
          }
        }
        break;
    }

    this._validationInfos.set(keyFrameMetadataEntry, validationInfo);
    this.forceUpdate();
  };

  onDeleteClick = (entry: KeyFrameMetadataEntry) => {
    const { dataTimeline } = this.props;
    dataTimeline.entries = dataTimeline.entries.filter((e) => e !== entry);
    this.forceUpdate();
  };

  onAddDatapointClick = () => {
    const { entries, frameRate } = this.props.dataTimeline;
    const startTime = entries[0].getStartTime();
    const endTime = entries[0].getEndTime();
    this.props.dataTimeline.addEntry(startTime, endTime, frameRate);
    this.forceUpdate();
  };

  onSubmit = () => {
    const { dropdownValue } = this.state;
    const { dataTimeline } = this.props;
    dataTimeline.name = dropdownValue;
    if (!this.isSaveDisabled()) this.props.onSubmit(dataTimeline);
  };

  isSaveDisabled = () => {
    const { dataTimeline } = this.props;
    const { dropdownValue, options } = this.state;

    const disabled =
      !options.find((o) => o.value === dropdownValue) ||
      !dataTimeline.entries.length ||
      dataTimeline.entries.reduce((val, entry) => {
        if (val === true) {
          return val;
        }

        const validationInfo = this._validationInfos.get(entry) || {};
        return (
          !entry.getMetadataMap().get(this.getKey()) ||
          validationInfo.startTimeMs === false ||
          validationInfo.stopTimeMs === false
        );
      }, false);

    return disabled;
  };

  onKeyPress = (event: KeyboardEvent) => {
    const key = event.keyCode || event.which;
    if (key === KeyCode.ENTER) {
      this.onSubmit();
    } else if (key === KeyCode.ESCAPE) {
      this.props.onClose();
    }
  };

  render() {
    const { dataTimeline, onClose, dataPointTarget } = this.props;
    const { dropdownValue, options } = this.state;
    const title = dataTimeline.id ? t('UPDATE_DATA_TIMELINE') : t('ADD_DATA_TIMELINE');

    const props = {
      name: 'DataTimelineDialog',
      dropdownValue,
      title,
      onClose,
      buttons: [
        { label: t('CANCEL'), type: 'CANCEL', onClick: onClose },
        { label: title, type: 'PRIMARY', onClick: this.onSubmit, disabled: this.isSaveDisabled() }
      ]
    };

    return (
      <Modal {...props} closeOnEsc closeOnOutsideClick>
        <div className="form block">
          <div className="form-field">
            <label>{t('NEW_TIMELINE_NAME')}</label>
            <Dropdown value={dropdownValue} options={options} onChange={this.onNameChange} autocomplete />
          </div>
        </div>
        {dataTimeline.entries.map((entry: KeyFrameMetadataEntry, i: number) => {
          const validationInfo = this._validationInfos.get(entry) || {};
          return (
            <TimelineDatapointForm
              key={i}
              index={i + 1}
              keyFrameMetadataEntry={entry}
              frameRate={dataTimeline.frameRate}
              startTimeInvalid={validationInfo.startTimeMs === false}
              stopTimeInvalid={validationInfo.stopTimeMs === false}
              onChange={(prop: string, value: string) => this.onDatapointChange(entry, prop, value)}
              onDeleteClick={() => this.onDeleteClick(entry)}
              videoController={this.props.videoController}
              groupName={this.getKey()}
              onSubmit={this.onSubmit}
            />
          );
        })}
        <div>
          <Button
            type="PLAIN"
            icon={ThemeSetup.getImageURL('plus-circle.svg')}
            text={t('ADD_DATA_POINT')}
            onClick={this.onAddDatapointClick}
            disabled={this.state.addDataPointDisabled}
          />
        </div>
      </Modal>
    );
  }
}
